import { useEffect, useState } from 'react';
import { useDailyBriefing } from '../hooks/useDailyBriefing.js';
import StepGreeting from './morning/StepGreeting.jsx';
import StepYesNo from './morning/StepYesNo.jsx';
import StepBedTime from './morning/StepBedTime.jsx';
import StepMITs from './morning/StepMITs.jsx';
import StepCalendarBlocks from './morning/StepCalendarBlocks.jsx';
import StepConfirmation from './morning/StepConfirmation.jsx';
import SummaryCard from './morning/SummaryCard.jsx';
import StreaksCard from './morning/StreaksCard.jsx';
import TitusPrincipleCard from './morning/TitusPrincipleCard.jsx';
import CheckOutWizard from './checkout/CheckOutWizard.jsx';

export default function MorningBriefing({ onSwitchTab }) {
  const {
    loading,
    briefing,
    topTasks,
    carriedForward,
    update,
    resumeStep,
    isComplete,
  } = useDailyBriefing();

  // Wizard step state — initialized once when loading finishes.
  const [step, setStep] = useState(1);
  const [initialized, setInitialized] = useState(false);
  const [calendarBlocks, setCalendarBlocks] = useState([]); // step 6 (ephemeral)
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => {
    if (!loading && !initialized) {
      setStep(resumeStep);
      setInitialized(true);
    }
  }, [loading, initialized, resumeStep]);

  if (loading) {
    return (
      <div className="text-center text-text-muted py-12">Loading…</div>
    );
  }

  if (isComplete) {
    return (
      <div>
        <SummaryCard
          briefing={briefing}
          onCheckOut={() => setCheckoutOpen(true)}
        />
        <StreaksCard />
        <TitusPrincipleCard />
        {checkoutOpen && (
          <CheckOutWizard onClose={() => setCheckoutOpen(false)} />
        )}
      </div>
    );
  }

  // ---- Step routing ----

  function go(n) {
    setStep(Math.max(1, Math.min(7, n)));
  }

  async function finish() {
    await update({ completed_at: new Date().toISOString() });
    // Brief beat for the user to see the confirmation result, then switch.
    setTimeout(() => onSwitchTab && onSwitchTab('dash'), 400);
  }

  let stepEl;
  switch (step) {
    case 1:
      stepEl = <StepGreeting onNext={() => go(2)} />;
      break;
    case 2:
      stepEl = (
        <StepYesNo
          step={2}
          title="Did you do your devotional?"
          subtitle="Honest answer — this is for you."
          value={briefing?.devotional}
          onChange={(v) => update({ devotional: v })}
          onBack={() => go(1)}
          onNext={() => go(3)}
        />
      );
      break;
    case 3:
      stepEl = (
        <StepYesNo
          step={3}
          title="Did you work out this morning?"
          subtitle="Counts if you broke a sweat."
          value={briefing?.workout}
          onChange={(v) => update({ workout: v })}
          onBack={() => go(2)}
          onNext={() => go(4)}
        />
      );
      break;
    case 4:
      stepEl = (
        <StepBedTime
          value={briefing?.bed_time}
          onChange={(t) => update({ bed_time: t })}
          onBack={() => go(3)}
          onNext={() => go(5)}
        />
      );
      break;
    case 5:
      stepEl = (
        <StepMITs
          briefing={briefing}
          topTasks={topTasks}
          carriedForward={carriedForward}
          onSave={(patch) => update(patch)}
          onBack={() => go(4)}
          onNext={() => go(6)}
        />
      );
      break;
    case 6:
      stepEl = (
        <StepCalendarBlocks
          briefing={briefing}
          onBlocksChange={setCalendarBlocks}
          onBack={() => go(5)}
          onNext={() => go(7)}
        />
      );
      break;
    case 7:
    default:
      stepEl = (
        <StepConfirmation
          briefing={briefing}
          calendarBlocks={calendarBlocks}
          onBack={() => go(6)}
          onFinish={finish}
        />
      );
      break;
  }

  return (
    <div>
      {stepEl}
      <StreaksCard />
    </div>
  );
}
